import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import CrudPage from "@/components/CrudPage";
import { FieldInput } from "@/components/CrudPage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus } from "lucide-react";

const UNITS = [
  { value: "sec_per_pc", label: "Saniye/Adet" },
  { value: "min_per_pc", label: "Dakika/Adet" },
  { value: "hr_per_pc", label: "Saat/Adet" },
  { value: "pcs_per_hr", label: "Adet/Saat" },
];

const unitLabel = (u) => UNITS.find((x) => x.value === u)?.label || u;

function Standards() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ products: [], operations: [], machines: [] });
  const [form, setForm] = useState({ product_code: "", operation_code: "", machine_code: "__none__", value: "", unit: "min_per_pc", cavities: 1, scrap_rate: 0, valid_from: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/standards").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/meta/options").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (n, v) => setForm((f) => ({ ...f, [n]: v }));

  const save = async () => {
    if (!form.product_code || !form.operation_code || !form.value) {
      toast.error("Ürün, operasyon ve standart değer zorunludur"); return;
    }
    setSaving(true);
    try {
      await api.post("/standards", {
        ...form,
        machine_code: !form.machine_code || form.machine_code === "__none__" ? null : form.machine_code,
        value: Number(form.value), cavities: Number(form.cavities) || 1,
        scrap_rate: Number(form.scrap_rate) || 0,
      });
      toast.success("Standart kaydedildi (yeni versiyon oluşturuldu)");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const fields = [
    { name: "product_code", label: "Ürün", type: "select", options: meta.products.map((p) => ({ value: p.code, label: `${p.code} — ${p.name}` })) },
    { name: "operation_code", label: "Operasyon", type: "select", options: meta.operations.map((o) => ({ value: o.code, label: o.name })) },
    { name: "machine_code", label: "Makine (opsiyonel)", type: "select", options: [{ value: "__none__", label: "— Genel —" }, ...meta.machines.map((m) => ({ value: m.code, label: `${m.code} — ${m.name}` }))] },
    { name: "unit", label: "Birim", type: "select", options: UNITS },
    { name: "value", label: "Standart Değer", type: "number" },
    { name: "cavities", label: "Göz Sayısı (enjeksiyon)", type: "number" },
    { name: "scrap_rate", label: "Fire Oranı %", type: "number" },
    { name: "valid_from", label: "Geçerlilik Tarihi", type: "date" },
  ];

  const prodName = (c) => meta.products.find((p) => p.code === c)?.name || c;
  const opName = (c) => meta.operations.find((o) => o.code === c)?.name || c;

  return (
    <div className="space-y-5" data-testid="standards-section">
      <Card className="p-4 bg-card border-border">
        <h3 className="text-lg font-medium mb-1">Yeni Standart / Versiyon</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Aynı ürün+operasyon için yeni kayıt, yeni versiyon oluşturur. Geçmiş üretim kayıtları eski standartla hesaplanmış olarak kalır.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {fields.map((f) => (
            <div key={f.name} className="space-y-1.5">
              <Label className="text-xs">{f.label}</Label>
              <FieldInput field={f} value={form[f.name]} onChange={set} testid={`standard-field-${f.name}`} />
            </div>
          ))}
        </div>
        <Button className="mt-4" onClick={save} disabled={saving} data-testid="standard-save-button">
          <Plus size={15} className="mr-1.5" /> {saving ? "Kaydediliyor..." : "Standart Ekle"}
        </Button>
      </Card>

      <Card className="bg-card border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              {["Ürün", "Operasyon", "Makine", "Değer", "Birim", "Göz", "Geçerlilik", "Versiyon"].map((h) => (
                <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((s, i) => (
              <TableRow key={`${s.product_code}-${s.operation_code}-${s.machine_code || "genel"}-v${s.version}-${s.valid_from}`} className="border-border" data-testid={`standard-row-${i}`}>
                <TableCell className="text-sm py-2">{s.product_code} — {prodName(s.product_code)}</TableCell>
                <TableCell className="text-sm py-2">{opName(s.operation_code)}</TableCell>
                <TableCell className="text-sm py-2">{s.machine_code || <span className="text-muted-foreground">Genel</span>}</TableCell>
                <TableCell className="text-sm py-2 font-mono">{s.value}</TableCell>
                <TableCell className="text-sm py-2">{unitLabel(s.unit)}</TableCell>
                <TableCell className="text-sm py-2 font-mono">{s.cavities || 1}</TableCell>
                <TableCell className="text-sm py-2 font-mono">{s.valid_from}</TableCell>
                <TableCell className="text-sm py-2"><Badge variant="outline" className="border-border font-mono">v{s.version}</Badge></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Standart bulunamadı</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export default function Operations() {
  const [meta, setMeta] = useState({ departments: [] });
  useEffect(() => { api.get("/meta/options").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="operations-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1">Operasyonlar & Standart Süreler</h1>
      <p className="text-sm text-muted-foreground mb-5">Kapasite hesaplama motorunun temelini oluşturan operasyon ve standart tanımları</p>
      <Tabs defaultValue="standards">
        <TabsList className="bg-secondary" data-testid="operations-tabs">
          <TabsTrigger value="standards" data-testid="tab-standards">Standart Süreler</TabsTrigger>
          <TabsTrigger value="operations" data-testid="tab-operations">Operasyonlar</TabsTrigger>
        </TabsList>
        <TabsContent value="standards" className="mt-4"><Standards /></TabsContent>
        <TabsContent value="operations" className="mt-4">
          <CrudPage
            title=""
            endpoint="operations"
            testidPrefix="operations"
            defaultForm={{ unit: "min_per_pc" }}
            columns={[
              { key: "code", label: "Kod" },
              { key: "name", label: "Operasyon Adı" },
              { key: "department_code", label: "Bölüm", render: (o) => meta.departments.find((d) => d.code === o.department_code)?.name || o.department_code },
              { key: "unit", label: "Varsayılan Birim", render: (o) => unitLabel(o.unit) },
            ]}
            fields={[
              { name: "code", label: "Operasyon Kodu", required: true, placeholder: "Örn: OP-CNC" },
              { name: "name", label: "Operasyon Adı", required: true },
              { name: "department_code", label: "Bölüm", type: "select", options: meta.departments.map((d) => ({ value: d.code, label: d.name })) },
              { name: "unit", label: "Varsayılan Birim", type: "select", options: UNITS },
            ]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
